import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIER_LABEL = ["Wanderer", "Poet", "Master", "Sensei"];

export default function KimonoRoom() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(() => {
    api.get("/kimono/me").then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/kimono/catalog").then((res) => setCatalog(res.data)).catch(() => {});
    if (user) loadStatus();
  }, [user, loadStatus]);

  const wear = async (k) => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/kimono/select", { kimono_id: k.id });
      toast.success(`You now wear the ${k.name} robe`);
      loadStatus();
      refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const subscribe = async (tier) => {
    if (!user) { navigate("/login"); return; }
    if (!tier.lookup_key) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (!catalog) return <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">Opening the wardrobe…</div>;

  const unlockLevel = status?.unlock_level ?? 0;
  const current = user?.kimono || status?.current_kimono;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="kimono-page">
      {/* header */}
      <div className="mb-14 max-w-2xl">
        <span className="hanko text-primary text-2xl">着物</span>
        <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter mt-2">Kimono Room</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          In Edo Japan, sumptuary law dictated the cloth one could wear by caste. Here, your robe
          is earned twice over — by the verses you link and by the rank of your membership. Choose
          the robe your name will carry through the house.
        </p>
      </div>

      {/* status band */}
      {user && status && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-16" data-testid="kimono-status">
          <div className="bg-background p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Membership</p>
            <p className="font-serif text-2xl mt-1">{TIER_LABEL[status.subscription_level]}</p>
          </div>
          <div className="bg-background p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Verses linked</p>
            <p className="font-serif text-2xl mt-1 text-secondary">{status.verse_count}</p>
          </div>
          <div className="bg-background p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Poetic rank</p>
            <p className="font-serif text-2xl mt-1">{status.rank_level} / 4</p>
          </div>
          <div className="bg-background p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Robes unlocked</p>
            <p className="font-serif text-2xl mt-1 text-primary">{status.unlocked.length} / {catalog.kimonos.length}</p>
          </div>
        </div>
      )}

      {/* wardrobe */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border mb-24">
        {catalog.kimonos.map((k, i) => {
          const locked = k.level > unlockLevel;
          const isCurrent = current === k.id;
          return (
            <motion.div
              key={k.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className={`bg-background flex flex-col ${isCurrent ? "ring-2 ring-primary ring-inset" : ""}`}
              data-testid={`kimono-card-${k.id}`}
            >
              <div className="relative bg-muted/40 border-b border-border">
                <img
                  src={k.image}
                  alt={`${k.name} kimono`}
                  className={`w-full h-72 object-contain p-3 transition-all duration-500 ${locked ? "grayscale opacity-40" : ""}`}
                />
                {locked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-foreground/60" />
                  </div>
                )}
                {isCurrent && (
                  <span className="absolute top-2 right-2 hanko bg-primary text-primary-foreground text-xs px-2 py-1">着</span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <p className="text-xs tracking-widest uppercase text-secondary">{k.caste}</p>
                <h3 className="font-serif text-2xl mt-1">{k.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{k.desc}</p>
                <p className="text-xs text-muted-foreground/80 mt-3 italic leading-relaxed border-l border-border pl-3">{k.law}</p>
                <div className="mt-auto pt-5">
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-primary">
                      <Check className="w-4 h-4" /> Now worn
                    </span>
                  ) : locked ? (
                    <span className="text-xs text-muted-foreground">Requires tier {k.level} rank or membership</span>
                  ) : (
                    <button
                      onClick={() => wear(k)}
                      data-testid={`wear-${k.id}`}
                      className="w-full border border-border py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
                    >
                      Wear this robe
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* subscription tiers */}
      <div className="mb-10">
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Rise in rank</h2>
        <p className="mt-3 text-muted-foreground max-w-xl">
          Membership grants immediate access to finer robes. Or keep to the Wanderer's path and
          earn every thread through your verses. Cancel anytime.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-border border border-border" data-testid="tiers">
        {catalog.tiers.map((tier) => {
          const active = status?.subscription_level === tier.level && tier.level > 0;
          const owned = (status?.subscription_level ?? 0) >= tier.level;
          return (
            <div key={tier.id} className={`bg-background p-8 flex flex-col ${tier.level === 3 ? "md:bg-muted/30" : ""}`} data-testid={`tier-${tier.id}`}>
              <p className="text-xs tracking-widest uppercase text-secondary">Tier {tier.level}</p>
              <h3 className="font-serif text-3xl mt-1">{tier.name}</h3>
              <p className="mt-3 text-4xl font-serif">
                {tier.price === 0 ? "Free" : <>${tier.price}<span className="text-base text-muted-foreground">/mo</span></>}
              </p>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">{tier.blurb}</p>
              <div className="mt-6">
                {tier.level === 0 ? (
                  <span className="text-sm text-muted-foreground">Everyone begins here</span>
                ) : active ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-primary"><Check className="w-4 h-4" /> Your tier</span>
                ) : owned ? (
                  <span className="text-sm text-muted-foreground">Already surpassed</span>
                ) : (
                  <button
                    onClick={() => subscribe(tier)}
                    disabled={busy}
                    data-testid={`subscribe-${tier.id}`}
                    className="w-full bg-primary text-primary-foreground py-3 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50"
                  >
                    {busy ? "…" : `Become ${tier.name}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!user && (
        <p className="mt-10 text-center text-muted-foreground">
          <Link to="/login" className="text-primary border-b border-primary/40">Enter the house</Link> to choose your robe.
        </p>
      )}
    </div>
  );
}
